import { useEffect, useState } from "react";

const DEFAULT_AVATAR = "/images/default-profile.png";

interface ProfileAvatarProps {
	/** Remote avatar URL — Google/LinkedIn CDN link, or empty for the default illustration. */
	src?: string;
	name?: string;
	className?: string;
	width?: number;
	height?: number;
}

/**
 * Avatar that reliably renders a Google profile picture.
 *
 * Google's `lh3.googleusercontent.com` CDN answers 403 when the browser sends a Referer from an
 * origin it doesn't know, so the image has to be requested with `referrerPolicy="no-referrer"`.
 * Anything else that fails to load (expired LinkedIn URL, offline) falls back to the default
 * illustration instead of the browser's broken-image alt text.
 */
function ProfileAvatar({ src, name, className = "", width, height }: ProfileAvatarProps) {
	const [failed, setFailed] = useState(false);

	// A different user (or a refreshed CDN URL) deserves another attempt.
	useEffect(() => {
		setFailed(false);
	}, [src]);

	return (
		<img
			src={!src || failed ? DEFAULT_AVATAR : src}
			alt={`${name || "User"}'s profile picture`}
			className={className}
			width={width}
			height={height}
			referrerPolicy="no-referrer"
			onError={() => setFailed(true)}
		/>
	);
}

export default ProfileAvatar;
